import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumLeadStatus } from "./enums/enumLeadStatus.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class LeadReadOnly extends ModelObject<LeadReadOnly> {

    public status: EnumLeadStatus = EnumLeadStatus.active;
    private _wonAt: Instant | null = null;
    /**
    * Timestamp at which the lead was marked as won.
    */
    public get wonAt(): Instant | null { return this._wonAt }
    public set wonAt(value: Instant | null) {
      this._wonAt = value === undefined ? null : value;
    }
    private _lostAt: Instant | null = null;
    /**
    * Timestamp at which the lead was marked as lost.
    */
    public get lostAt(): Instant | null { return this._lostAt }
    public set lostAt(value: Instant | null) {
      this._lostAt = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the lead was last updated.
    */
    public lastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    private _latitude: number | null = null;
    /**
    * Latitude
    */
    public get latitude(): number | null { return this._latitude }
    public set latitude(value: number | null) {
      this._latitude = value === undefined ? null : value;
    }
    private _longitude: number | null = null;
    /**
    * Longitude
    */
    public get longitude(): number | null { return this._longitude }
    public set longitude(value: number | null) {
      this._longitude = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumLeadStatus.getByValue(jsonSerializedObject.status, this.status);
          this.wonAt = jsonSerializedObject.wonAt == null ? null : Instant.parse(jsonSerializedObject.wonAt);
          this.lostAt = jsonSerializedObject.lostAt == null ? null : Instant.parse(jsonSerializedObject.lostAt);
          this.lastUpdatedAt = Instant.parse(jsonSerializedObject.lastUpdatedAt);
          this.latitude = jsonSerializedObject.latitude == null ? null : jsonSerializedObject.latitude;
          this.longitude = jsonSerializedObject.longitude == null ? null : jsonSerializedObject.longitude;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        toRet.wonAt = this.wonAt == null ? null : this.wonAt.toString();
        toRet.lostAt = this.lostAt == null ? null : this.lostAt.toString();
        toRet.lastUpdatedAt = this.lastUpdatedAt.toString();
        toRet.latitude = this.latitude == null ? null : this.latitude;
        toRet.longitude = this.longitude == null ? null : this.longitude;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Lead extends ModelObjectWithDirtyCheck<Lead> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Lead business name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public businessName: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Lead contact name
    * Not Nullable
    * Max Length : 1000
    */
    public contactName: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Notes about the lead
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    private _salesCodeId: Guid | null = null;
    /**
    * Sales Code Id
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    private _salesAreaId: Guid | null = null;
    /**
    * Sales Area Id
    */
    public get salesAreaId(): Guid | null { return this._salesAreaId }
    public set salesAreaId(value: Guid | null) {
      this._salesAreaId = value === undefined ? null : value;
    }
    private _assignedToUserId: Guid | null = null;
    /**
    * User, if any, currently assigned to this lead.
    */
    public get assignedToUserId(): Guid | null { return this._assignedToUserId }
    public set assignedToUserId(value: Guid | null) {
      this._assignedToUserId = value === undefined ? null : value;
    }
    /**
    * The monetary value of the lead. For display and reporting purposes.
    */
    public value: number = 0;
    /**
    * The probability of winning this lead (creating a customer from it). For display and reporting purposes.
    * Not Negative
    */
    public winProbabilityPercentage: number = 0;
    private _leadSourceId: Guid | null = null;
    /**
    * The source that generated this lead.
    */
    public get leadSourceId(): Guid | null { return this._leadSourceId }
    public set leadSourceId(value: Guid | null) {
      this._leadSourceId = value === undefined ? null : value;
    }
    private _startDate: LocalDate | null = null;
    /**
    * The date which the lead became pursued. For reporting purposes only.
    */
    public get startDate(): LocalDate | null { return this._startDate }
    public set startDate(value: LocalDate | null) {
      this._startDate = value === undefined ? null : value;
    }
    private _estimatedClosingDate: LocalDate | null = null;
    /**
    * The date which is is expected for the lead to be closed (won or lost). For reporting purposes only.
    */
    public get estimatedClosingDate(): LocalDate | null { return this._estimatedClosingDate }
    public set estimatedClosingDate(value: LocalDate | null) {
      this._estimatedClosingDate = value === undefined ? null : value;
    }
    private _wonOutletId: Guid | null = null;
    /**
    * The outlet that was won as a result of this lead.
    */
    public get wonOutletId(): Guid | null { return this._wonOutletId }
    public set wonOutletId(value: Guid | null) {
      this._wonOutletId = value === undefined ? null : value;
    }
    private _leadLossReasonId: Guid | null = null;
    /**
    * The reason this lead was lost (not converted to an outlet).
    */
    public get leadLossReasonId(): Guid | null { return this._leadLossReasonId }
    public set leadLossReasonId(value: Guid | null) {
      this._leadLossReasonId = value === undefined ? null : value;
    }
    /**
    * More descriptive notes to why the lead was lost.
    * Not Nullable
    * Max Length : 100000
    */
    public lossReasonNotes: string = "";
    /**
    * The current pipeline stage for this lead.
    */
    public pipelineStageId: Guid = Guid.createEmpty();
    /**
    * Whether the current lead is on hold.
    */
    public isOnHold: boolean = false;
    public isHidden: boolean = false;
    public readOnly: LeadReadOnly = new LeadReadOnly();
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Contact Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressPostcode: string = "";
    public webAddress: string = "";
    private _outletTypeId: Guid | null = null;
    public get outletTypeId(): Guid | null { return this._outletTypeId }
    public set outletTypeId(value: Guid | null) {
      this._outletTypeId = value === undefined ? null : value;
    }
    /**
    * Liquor Licence Number, only show for Australia and US region.
    * Not Nullable
    * Max Length : 1000
    */
    public liquorLicenceNumber: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.businessName = jsonSerializedObject.businessName;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.contactName = jsonSerializedObject.contactName;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.notes = jsonSerializedObject.notes;
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.salesAreaId = jsonSerializedObject.salesAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesAreaId);
          this.assignedToUserId = jsonSerializedObject.assignedToUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.assignedToUserId);
          this.value = jsonSerializedObject.value;
          this.winProbabilityPercentage = jsonSerializedObject.winProbabilityPercentage;
          this.leadSourceId = jsonSerializedObject.leadSourceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.leadSourceId);
          this.startDate = jsonSerializedObject.startDate == null ? null : LocalDate.parse(jsonSerializedObject.startDate);
          this.estimatedClosingDate = jsonSerializedObject.estimatedClosingDate == null ? null : LocalDate.parse(jsonSerializedObject.estimatedClosingDate);
          this.wonOutletId = jsonSerializedObject.wonOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.wonOutletId);
          this.leadLossReasonId = jsonSerializedObject.leadLossReasonId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.leadLossReasonId);
          this.lossReasonNotes = jsonSerializedObject.lossReasonNotes;
          this.pipelineStageId = Guid.parseOrEmpty(jsonSerializedObject.pipelineStageId);
          this.isOnHold = jsonSerializedObject.isOnHold;
          this.isHidden = jsonSerializedObject.isHidden;
          this.readOnly = new LeadReadOnly(jsonSerializedObject.readOnly);
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.addressPostcode = jsonSerializedObject.addressPostcode;
          this.webAddress = jsonSerializedObject.webAddress;
          this.outletTypeId = jsonSerializedObject.outletTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.liquorLicenceNumber = jsonSerializedObject.liquorLicenceNumber;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.businessName = this.businessName;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.contactName = this.contactName;
        toRet.emailAddress = this.emailAddress;
        toRet.notes = this.notes;
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.salesAreaId = this.salesAreaId == null ? null : this.salesAreaId.toString();
        toRet.assignedToUserId = this.assignedToUserId == null ? null : this.assignedToUserId.toString();
        toRet.value = this.value;
        toRet.winProbabilityPercentage = this.winProbabilityPercentage;
        toRet.leadSourceId = this.leadSourceId == null ? null : this.leadSourceId.toString();
        toRet.startDate = this.startDate == null ? null : this.startDate.toString();
        toRet.estimatedClosingDate = this.estimatedClosingDate == null ? null : this.estimatedClosingDate.toString();
        toRet.wonOutletId = this.wonOutletId == null ? null : this.wonOutletId.toString();
        toRet.leadLossReasonId = this.leadLossReasonId == null ? null : this.leadLossReasonId.toString();
        toRet.lossReasonNotes = this.lossReasonNotes;
        toRet.pipelineStageId = this.pipelineStageId.toString();
        toRet.isOnHold = this.isOnHold;
        toRet.isHidden = this.isHidden;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.addressPostcode = this.addressPostcode;
        toRet.webAddress = this.webAddress;
        toRet.outletTypeId = this.outletTypeId == null ? null : this.outletTypeId.toString();
        toRet.liquorLicenceNumber = this.liquorLicenceNumber;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Lead.Id cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length !== 0 || "BusinessName cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length <= 1000 || "BusinessName has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "contactName", rule: (v: any) => v == null || v.length <= 1000 || "ContactName has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.SalesCodeId cannot be empty"},
            {field: "salesAreaId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.SalesAreaId cannot be empty"},
            {field: "assignedToUserId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.AssignedToUserId cannot be empty"},
            {field: "leadSourceId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.LeadSourceId cannot be empty"},
            {field: "wonOutletId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.WonOutletId cannot be empty"},
            {field: "leadLossReasonId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.LeadLossReasonId cannot be empty"},
            {field: "lossReasonNotes", rule: (v: any) => v == null || v.length <= 100000 || "LossReasonNotes has too many characters (100000)"},
            {field: "pipelineStageId", rule: (v: any) => !v.isEmpty() || "Lead.PipelineStageId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "addressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "AddressPostcode has too many characters (1000)"},
            {field: "webAddress", rule: (v: any) => v == null || v.length <= 1000 || "WebAddress has too many characters (1000)"},
            {field: "outletTypeId", rule: (v: any) => v == null || !v.isEmpty() || "Lead.OutletTypeId cannot be empty"},
            {field: "liquorLicenceNumber", rule: (v: any) => v == null || v.length <= 1000 || "LiquorLicenceNumber has too many characters (1000)"},
    ];
}


export class LeadFilter extends ModelObject<LeadFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _statuses: readonly (EnumLeadStatus)[] | null = null;
    public get statuses(): readonly (EnumLeadStatus)[] | null { return this._statuses }
    public set statuses(value: readonly (EnumLeadStatus)[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _assignedUserIds: readonly (Guid)[] | null = null;
    public get assignedUserIds(): readonly (Guid)[] | null { return this._assignedUserIds }
    public set assignedUserIds(value: readonly (Guid)[] | null) {
      this._assignedUserIds = value === undefined ? null : value;
    }
    private _leadPipelineStageIds: readonly (Guid)[] | null = null;
    public get leadPipelineStageIds(): readonly (Guid)[] | null { return this._leadPipelineStageIds }
    public set leadPipelineStageIds(value: readonly (Guid)[] | null) {
      this._leadPipelineStageIds = value === undefined ? null : value;
    }
    private _wonOutletIds: readonly (Guid)[] | null = null;
    public get wonOutletIds(): readonly (Guid)[] | null { return this._wonOutletIds }
    public set wonOutletIds(value: readonly (Guid)[] | null) {
      this._wonOutletIds = value === undefined ? null : value;
    }
    public assignedOrNot: EnumYesNoEither = EnumYesNoEither.either;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumLeadStatus.getByValue(v));
          this.assignedUserIds = jsonSerializedObject.assignedUserIds == null ? null : jsonSerializedObject.assignedUserIds.map((v: any) => Guid.parseOrEmpty(v));
          this.leadPipelineStageIds = jsonSerializedObject.leadPipelineStageIds == null ? null : jsonSerializedObject.leadPipelineStageIds.map((v: any) => Guid.parseOrEmpty(v));
          this.wonOutletIds = jsonSerializedObject.wonOutletIds == null ? null : jsonSerializedObject.wonOutletIds.map((v: any) => Guid.parseOrEmpty(v));
          this.assignedOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.assignedOrNot, this.assignedOrNot);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.assignedUserIds = this.assignedUserIds == null ? null : this.assignedUserIds.map(v => v.toString());
        toRet.leadPipelineStageIds = this.leadPipelineStageIds == null ? null : this.leadPipelineStageIds.map(v => v.toString());
        toRet.wonOutletIds = this.wonOutletIds == null ? null : this.wonOutletIds.map(v => v.toString());
        toRet.assignedOrNot = this.assignedOrNot.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredLeadResults extends ModelObject<FilteredLeadResults> {

    public matchingCount: number = 0;
    public leads: (Lead)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.leads = jsonSerializedObject.leads.map((v: any) => new Lead(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.leads = this.leads.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "leads", rule: (v: any) => !!v || "leads is required"},
            {field: "leads", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Lead[]).reduce((acc: Array<string>, curr: Lead) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class LeadService extends ServiceBase {

  public async getAllLeads(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Lead)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.leads.map((v: any) => new Lead(v));;
    return this.makeJsonRequest("lead/v1/getAllLeads", req, false, extractor, opts);
  };

  public async getLead(leadId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Lead> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadId:leadId.toString(),
    };
    const extractor = (response:any) => new Lead(response.lead);;
    return this.makeJsonRequest("lead/v1/getLead", req, false, extractor, opts);
  };

  public async getLeads(leadIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Lead)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadIds:leadIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.leads.map((v: any) => new Lead(v));;
    return this.makeJsonRequest("lead/v1/getLeads", req, false, extractor, opts);
  };

  public async getLeadsByFilter(filter: LeadFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredLeadResults> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredLeadResults(response.results);;
    return this.makeJsonRequest("lead/v1/getLeadsByFilter", req, false, extractor, opts);
  };

  public async saveLead(lead: Lead, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    lead:lead.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("lead/v1/saveLead", req, true, extractor, opts);
  };

  public async saveLeads(leads: readonly Lead[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    leads:leads.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("lead/v1/saveLeads", req, true, extractor, opts);
  };

  public async setLeadIsHidden(leadId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadId:leadId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("lead/v1/setLeadIsHidden", req, true, extractor, opts);
  };
}

export const leadService = new LeadService();